import { Component, Element, Event, EventEmitter, Fragment, h, Method, Prop, State, Watch } from '@stencil/core';
import {
  Calendar,
  CalendarDoc,
  DateUtils,
  FileDoc,
  FilePageDoc,
  FileType,
  MarkupDoc,
  PersonDoc,
  PhotoDoc,
  ProjectDoc,
  SubmittalDoc,
  SubmittalFeedItemDoc,
  SubmittalRevision,
} from 'common';
import { DocumentViewerModal } from 'src/components/concourse-documents/document-viewer-modal';
import { PersonInfoModal } from 'src/components/concourse-person-selector/person-info-modal';
import { ConcourseGalleryModal } from 'src/components/concourse-photos/concourse-gallery-modal';
import { CalendarUtils } from 'src/components/concourse-schedule/utils/CalendarUtils';
import { ChangeEvent, DbListener, PeopleService, SharedUtils } from 'src/global';

export interface DocumentViewerPreviewPage {
  id: string;
  label: string;
  thumb?: string;
  svg?: string;
  thumb_width?: number;
  thumb_height?: number;
  showFileSeparator?: boolean;
  separatorLabel?: string;
}

@Component({
  tag: 'submittal-files-viewer',
  styleUrl: 'submittal-files-viewer.css',
})
export class SubmittalFilesViewer {
  @Element() el: HTMLElement;

  @Prop() project: ProjectDoc;
  @Prop() canWrite: boolean = false;
  @Prop() fileRoles: string[] = [];
  @Prop() fileType: FileType = FileType.DOCUMENT;
  @Prop() files: FileDoc[] = [];
  @Prop() submittal: SubmittalDoc;
  @Prop() calendar: Calendar;
  @Prop() people: PersonDoc[];
  @Prop() unacceptedFiles: { feedItemId: string; file: FileDoc }[] = [];
  @Prop() unacceptedPhotos: { feedItemId: string; file: FileDoc }[] = [];
  @Prop() initialPages: string[] = [];
  @Prop() externalPages?: FilePageDoc[];
  @Prop() externalMarkups?: MarkupDoc[];
  @Prop() pageId: string = '';
  @Prop() revisionFeed: SubmittalFeedItemDoc[] = [];

  @Event() pageInfoChange: EventEmitter<{ currentIndex: number; total: number }>;
  @Event() toggleAcceptedFile: EventEmitter<string>;
  @Event() markupChanged: EventEmitter<MarkupDoc>;
  @Event() fileAdded: EventEmitter<FileDoc>;
  @Event() revisionChange: EventEmitter<SubmittalRevision>;
  @Event() dueDateClick: EventEmitter<number>;

  @State() pages: FilePageDoc[] = [];
  @State() markups: MarkupDoc[] = [];
  @State() pendingUploads: File[] = [];
  @State() workflowVisible: boolean = true;
  @State() scrollToEnd: boolean = false;
  @State() currentZoom: number = 1;

  private viewerEl?: HTMLFilePagesViewerElement;

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  componentWillLoad() {
    this.pages = this.externalPages ?? [];
    this.markups = this.externalMarkups ?? [];
  }

  componentDidLoad() {}

  @Watch('externalPages')
  onExternalPagesChange(newPages: FilePageDoc[]) {
    this.pages = newPages ?? [];
  }

  @Watch('externalMarkups')
  onExternalMarkupsChange(newMarkups: MarkupDoc[]) {
    this.markups = newMarkups ?? [];
  }

  @Watch('pageId')
  onPageIdChange() {
    if (this.pageId) {
      void this.viewerEl?.scrollToPageId(this.pageId);
    }
  }

  @Method()
  async zoomIn(): Promise<void> {
    await this.viewerEl?.zoomIn();
  }

  @Method()
  async zoomOut(): Promise<void> {
    await this.viewerEl?.zoomOut();
  }

  @Method()
  async nextPage(): Promise<void> {
    const { currentIndex, total } = await this.getPageInfo();
    if (currentIndex < total - 1) {
      await this.goToPageIndex(currentIndex + 1);
    }
  }

  @Method()
  async prevPage(): Promise<void> {
    const { currentIndex } = await this.getPageInfo();
    if (currentIndex > 0) {
      await this.goToPageIndex(currentIndex - 1);
    }
  }

  @Method()
  async getPageInfo(): Promise<{ currentIndex: number; total: number }> {
    const total = this.getInitialPageCount() + (this.pages?.length ?? 0);
    if (total === 0) {
      return { currentIndex: 0, total: 0 };
    }

    let currentIndex = 0;
    if (this.pageId?.startsWith('__initial_')) {
      const initialIndex = Number.parseInt(this.pageId.replace('__initial_', ''), 10);
      currentIndex = Number.isFinite(initialIndex) ? initialIndex : 0;
    } else if (this.pageId) {
      const fileIndex = this.pages.findIndex(p => p._id === this.pageId);
      currentIndex = fileIndex >= 0 ? this.getInitialPageCount() + fileIndex : 0;
    }

    return { currentIndex, total };
  }

  @Method()
  async goToPageIndex(pageIndex: number): Promise<void> {
    await this.viewerEl?.scrollToPageIndex(pageIndex);
  }

  private getInitialPageCount(): number {
    return (this.initialPages ?? []).filter(page => !!page?.trim()).length;
  }

  private onPageIndexChanged = (event: CustomEvent<number>) => {
    const currentIndex = event.detail;
    if (currentIndex === undefined || currentIndex === null || currentIndex < 0) {
      return;
    }

    const total = this.getInitialPageCount() + (this.pages?.length ?? 0);
    this.pageInfoChange.emit({ currentIndex, total });
  };

  private static readonly SCROLL_DIRECTION_THRESHOLD = 10;

  private onViewportScroll = (event: CustomEvent<{ scrollTop: number; deltaY: number; isProgrammatic: boolean; isAtEnd: boolean }>) => {
    const { scrollTop, deltaY, isProgrammatic, isAtEnd } = event.detail;

    this.scrollToEnd = isAtEnd;

    if (isProgrammatic) {
      return;
    }
    if (scrollTop <= 0) {
      this.workflowVisible = true;
      return;
    }
    if (deltaY > SubmittalFilesViewer.SCROLL_DIRECTION_THRESHOLD) {
      this.workflowVisible = false;
    } else if (deltaY < -SubmittalFilesViewer.SCROLL_DIRECTION_THRESHOLD) {
      this.workflowVisible = true;
    }
  };

  private onViewportZoom = (event: CustomEvent<number>) => {
    this.currentZoom = event.detail;
  };

  private fileSeparatorCallback = (fileId: string) => {
    const file = this.files?.find(f => f._id === fileId);
    if (file) {
      return (
        <Fragment>
          <span class="document-separator__label">
            {file.name} | {file.num_pages} {file.num_pages === 1 ? 'page' : 'pages'}
          </span>
        </Fragment>
      );
    }
    return null;
  };

  /**
   * This method just creates space at the bottom so that the uploader can be visible.
   */
  private footerContentCallback = () => {
    const photosHeight = this.unacceptedPhotos.length > 0 ? 180 : 0;
    const height = (150 + this.unacceptedFiles.length * 120 + photosHeight) / this.currentZoom;
    return <div style={{ height: `${height}px` }}></div>;
  };

  showPerson(event: Event, person: PersonDoc) {
    event.stopImmediatePropagation();
    PersonInfoModal.presentModal(person);
  }

  showFile(file: FileDoc) {
    DocumentViewerModal.present(this.project._id, file, false);
  }

  showPhoto(photo: FileDoc) {
    const author = PeopleService.findPerson(this.people, photo.created_by);
    ConcourseGalleryModal.present(this.project._id, `Photo by ${author?.first_name || 'Unknown'}`, [photo as PhotoDoc]);
  }

  async addPendingFiles(files: File[]) {
    const transfer = new DataTransfer();
    files.forEach(f => transfer.items.add(f));
    this.pendingUploads = await SharedUtils.filterAndValidateUploads(transfer.files, this.pendingUploads);
  }

  onFileUploaded(file: FileDoc, source: File) {
    this.pendingUploads = this.pendingUploads.filter(f => f !== source);
    this.fileAdded.emit(file);
  }

  render() {
    if (!this.submittal || !this.revisionFeed || !this.people || !this.calendar) return null;

    return (
      <div class="submittal-files-viewer">
        <div class={`submittal-files-viewer__workflow ${this.workflowVisible ? '' : 'submittal-files-viewer__workflow--hidden'}`}>
          <div class="ion-padding-horizontal">
            <submittal-timeline
              projectId={this.project._id}
              submittal={this.submittal}
              feed={this.revisionFeed}
              people={this.people}
              calendar={this.calendar}
              canWrite={this.canWrite}
              onRevisionChange={e => this.revisionChange.emit(e.detail)}
              onDueDateClick={e => this.dueDateClick.emit(e.detail)}
            />
          </div>
        </div>
        <file-pages-viewer
          ref={el => (this.viewerEl = el as HTMLFilePagesViewerElement)}
          style={{ display: 'block', height: '100%' }}
          projectId={this.project._id}
          pages={this.pages}
          markups={this.markups}
          initialPages={this.initialPages}
          toolsDisabled={!this.canWrite}
          referenceToolsEnabled={false}
          imageInsertEnabled={false}
          fileSeparatorCallback={this.fileSeparatorCallback}
          footerContentCallback={this.footerContentCallback}
          onPageIndexChanged={this.onPageIndexChanged}
          onViewportScroll={this.onViewportScroll}
          onViewportZoom={this.onViewportZoom}
          onMarkupChanged={e => this.markupChanged.emit(e.detail)}
        />
        {!this.isMobile && this.renderFooterWithUploader()}
      </div>
    );
  }

  renderFooterWithUploader() {
    if (this.isMobile) return null;
    return (
      <div class={`submittal-files-viewer__uploader ${this.scrollToEnd ? '' : 'ion-display-none'}`}>
        {(this.unacceptedFiles.length > 0 || this.unacceptedPhotos.length > 0) && (
          <ion-card-header>
            <ion-card-title class="typography-body1-500">Unaccepted Files</ion-card-title>
          </ion-card-header>
        )}

        {this.unacceptedFiles.map(({ feedItemId, file }) => {
          const person = PeopleService.findPerson(this.people, file.created_by);
          return (
            <Fragment>
              <file-preview-card
                file={file}
                person={person}
                onFileClick={({ detail }) => this.showFile(detail.file)}
                onPersonClick={({ detail }) => this.showPerson(event, detail)}
              >
                {this.canWrite && <feed-item-accept-button slot="button" isAccepted={false} onToggleAccepted={e => this.toggleAcceptedFile.emit(feedItemId)} />}
              </file-preview-card>
            </Fragment>
          );
        })}
        {this.unacceptedPhotos.length > 0 && (
          <div class="submittal-files-viewer__unaccepted-photos">
            {this.unacceptedPhotos.map(({ feedItemId, file }) => {
              const person = PeopleService.findPerson(this.people, file.created_by);
              const personName = person ? PeopleService.getFullName(person) : 'Unknown User';
              const createdDate = file.created_date ? DateUtils.formatIso(file.created_date) : '';
              const imgUrl = file.thumb ? `data:image/jpeg;base64,${file.thumb}` : null;
              return (
                <div class="submittal-files-viewer__unaccepted-photo">
                  <div
                    class="submittal-files-viewer__unaccepted-photo-thumb"
                    style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
                    onClick={() => this.showPhoto(file)}
                  >
                    <feed-item-accept-button isAccepted={false} iconOnly={true} onToggleAccepted={e => this.toggleAcceptedFile.emit(feedItemId)} />
                  </div>
                  <div class="submittal-files-viewer__unaccepted-photo-meta">
                    <div class="submittal-files-viewer__unaccepted-photo-name">{personName}</div>
                    {createdDate && <div class="submittal-files-viewer__unaccepted-photo-date">{createdDate}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {this.pendingUploads.map(file =>
          file.type?.startsWith('image') ? (
            <photo-uploader-instance projectId={this.project._id} upload={file} onUploaded={e => this.onFileUploaded(e.detail, file)} />
          ) : (
            <file-uploader-instance
              layout="square"
              projectId={this.project._id}
              file={file}
              fileType={this.fileType}
              fileRoles={this.fileRoles}
              onUploaded={e => this.onFileUploaded(e.detail, file)}
              onCancel={() => (this.pendingUploads = this.pendingUploads.filter(f => f !== file))}
            />
          ),
        )}
        {this.canWrite && (
          <concourse-file-uploader projectId={this.project._id} style={{ width: '100%' }} multiple={true} onFilesSelected={e => void this.addPendingFiles(e.detail)} />
        )}
      </div>
    );
  }
}
